'use client';

import React from 'react';
import {
  CheckCircledIcon,
  CrossCircledIcon,
  ClockIcon,
  DotFilledIcon,
} from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Text } from '../text';
import { Badge } from '../badge';
// import { Icon } from '../icon';

export interface TimelineItemProps extends BoxProps {
  /** Item title */
  title: string;
  /** Item description */
  description?: React.ReactNode;
  /** Timestamp */
  time?: string;
  /** Icon to display in the marker */
  icon?: React.ReactNode;
  /** Marker color */
  markerColor?: string;
  /** Status for the item */
  status?: 'completed' | 'active' | 'pending' | 'error';
  /** Custom marker content */
  customMarker?: React.ReactNode;
  /** Additional content to display */
  additionalContent?: React.ReactNode;
  /** Tags to display */
  tags?: string[];
  /** Props passed from Timeline parent */
  timelineOrientation?: 'vertical' | 'horizontal';
  showConnector?: boolean;
  connectorStyle?: 'solid' | 'dashed' | 'dotted';
  connectorColor?: string;
  alignment?: 'left' | 'center' | 'right';
  isFirst?: boolean;
  isLast?: boolean;
  index?: number;
}

/**
 * TimelineItem component - individual item in a timeline
 * Used inside Timeline component
 */
export const TimelineItem: React.FC<TimelineItemProps> = ({
  title,
  description,
  time,
  icon,
  markerColor,
  status,
  customMarker,
  additionalContent,
  tags,
  timelineOrientation = 'vertical',
  showConnector = true,
  connectorStyle = 'solid',
  connectorColor = 'border.muted',
  alignment = 'left',
  isFirst: _isFirst = false,
  isLast: _isLast = false,
  index: _index = 0,
  ...boxProps
}) => {
  const isVertical = timelineOrientation === 'vertical';

  // Get status-based defaults
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircledIcon />;
      case 'error':
        return <CrossCircledIcon />;
      case 'active':
        return <ClockIcon />;
      default:
        return <DotFilledIcon />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return 'bg.success';
      case 'error':
        return 'bg.error';
      case 'active':
        return 'bg.info';
      case 'pending':
        return 'bg.muted';
      default:
        return markerColor || 'bg.primary';
    }
  };

  const defaultIcon = icon || getStatusIcon();
  const defaultMarkerColor = getStatusColor();

  // Render marker
  const renderMarker = () => {
    if (customMarker) {
      return customMarker;
    }

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={10}
        height={10}
        borderRadius="full"
        backgroundColor={defaultMarkerColor}
        color="white"
        boxShadow="sm"
        border="3px solid"
        borderColor="bg.surface"
        position="relative"
        zIndex={2}
      >
        {defaultIcon}
      </Box>
    );
  };

  // Render connector line
  const renderConnector = () => {
    if (!showConnector) return null;

    const lineStyle = {
      backgroundColor: connectorColor,
      borderStyle: connectorStyle,
    };

    if (isVertical) {
      return (
        <Box
          position="absolute"
          left="50%"
          top="40px"
          bottom="-20px"
          width="2px"
          transform="translateX(-50%)"
          zIndex={1}
          {...lineStyle}
        />
      );
    } else {
      return (
        <Box
          position="absolute"
          top="50%"
          left="40px"
          right="-20px"
          height="2px"
          transform="translateY(-50%)"
          zIndex={1}
          {...lineStyle}
        />
      );
    }
  };

  // Layout based on alignment
  if (isVertical) {
    if (alignment === 'center') {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" {...boxProps}>
          <Box position="relative">
            {renderMarker()}
            {renderConnector()}
          </Box>

          <Box marginTop={3} textAlign="center" maxWidth="400px">
            {time && (
              <Text fontSize="sm" color="fg.muted" marginBottom={1}>
                {time}
              </Text>
            )}
            <Text fontWeight="semibold" marginBottom={1}>
              {title}
            </Text>
            {description && (
              <Text fontSize="sm" color="fg.muted" marginBottom={2}>
                {description}
              </Text>
            )}
            {tags && tags.length > 0 && (
              <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap" marginBottom={2}>
                {tags.map((tag, i) => (
                  <Badge key={i} size="sm" colorPalette="gray">
                    {tag}
                  </Badge>
                ))}
              </Box>
            )}
            {additionalContent}
          </Box>
        </Box>
      );
    }

    const isRight = alignment === 'right';

    return (
      <Box
        display="grid"
        gridTemplateColumns={isRight ? '1fr auto' : 'auto 1fr'}
        gap={4}
        alignItems="flex-start"
        {...boxProps}
      >
        {isRight && (
          <Box textAlign="right">
            {time && (
              <Text fontSize="sm" color="fg.muted" marginBottom={1}>
                {time}
              </Text>
            )}
            <Text fontWeight="semibold" marginBottom={1}>
              {title}
            </Text>
            {description && (
              <Text fontSize="sm" color="fg.muted" marginBottom={2}>
                {description}
              </Text>
            )}
            {tags && tags.length > 0 && (
              <Box
                display="flex"
                gap={1}
                justifyContent="flex-end"
                flexWrap="wrap"
                marginBottom={2}
              >
                {tags.map((tag, i) => (
                  <Badge key={i} size="sm" colorPalette="gray">
                    {tag}
                  </Badge>
                ))}
              </Box>
            )}
            {additionalContent}
          </Box>
        )}

        <Box position="relative">
          {renderMarker()}
          {renderConnector()}
        </Box>

        {!isRight && (
          <Box>
            {time && (
              <Text fontSize="sm" color="fg.muted" marginBottom={1}>
                {time}
              </Text>
            )}
            <Text fontWeight="semibold" marginBottom={1}>
              {title}
            </Text>
            {description && (
              <Text fontSize="sm" color="fg.muted" marginBottom={2}>
                {description}
              </Text>
            )}
            {tags && tags.length > 0 && (
              <Box display="flex" gap={1} flexWrap="wrap" marginBottom={2}>
                {tags.map((tag, i) => (
                  <Badge key={i} size="sm" colorPalette="gray">
                    {tag}
                  </Badge>
                ))}
              </Box>
            )}
            {additionalContent}
          </Box>
        )}
      </Box>
    );
  }

  // Horizontal layout
  return (
    <Box display="flex" alignItems="center" gap={3} {...boxProps}>
      <Box position="relative">
        {renderMarker()}
        {renderConnector()}
      </Box>

      <Box>
        <Box display="flex" alignItems="center" gap={2} marginBottom={1}>
          <Text fontWeight="semibold">{title}</Text>
          {time && (
            <Text fontSize="sm" color="fg.muted">
              {time}
            </Text>
          )}
        </Box>
        {description && (
          <Text fontSize="sm" color="fg.muted" marginBottom={2}>
            {description}
          </Text>
        )}
        {tags && tags.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap" marginBottom={2}>
            {tags.map((tag, i) => (
              <Badge key={i} size="sm" colorPalette="gray">
                {tag}
              </Badge>
            ))}
          </Box>
        )}
        {additionalContent}
      </Box>
    </Box>
  );
};

export default TimelineItem;
